import React, { Fragment, Component } from "react";
import { show, hide } from "redux-modal";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import {
  Button,
  Frame,
  Body,
  DisplayMedium,
  spacing,
  typography,
} from "@conserveit/blocks";
import { connect } from "react-redux";
import { Trans, t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import styled from "styled-components";
import { ChangePasswordForm, MsgAlert } from "../../components";
import ChangePasswordModal, {
  CHANGE_PASSWORD_MODAL,
} from "../ChangePasswordModal";
import * as profileActions from "../../redux/modules/profile/actionCreators";
import LanguageSelect from "../LanguageSelect";

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  grid-column-gap: ${spacing.default};
  margin-bottom: ${spacing.default};

  .key {
    ${typography.label}
  }
`;
const StyledPageTitle = styled(DisplayMedium)`
  margin-bottom: ${spacing.loose};
  text-align: center;
`;
const StyledFrameContent = styled.div`
  min-height: 420px;
  margin: ${spacing.extraLoose};
`;

const ProfileKeyValue = ({ keyText, valueText }) => (
  <StyledRow>
    <Body className="key">{keyText}</Body>
    {React.isValidElement(valueText) ? (
      <div className="value">{valueText}</div>
    ) : (
      <Body className="value">{valueText}</Body>
    )}
  </StyledRow>
);

@connect(
  (state) => ({
    user: state.auth.user,
    working: state.profile.working,
    changing_password: state.profile.changing_password,
    modal_data: state.profile.modal_data,
    profile_form_data: state.profile.profile_form_data,
    error: state.profile.error,
    success: state.profile.success,
  }),
  (dispatch) => ({
    ...profileActions,
    handleSubmit: () => {
      dispatch(profileActions.changePasswordRequest());
    },
    handleChange: (e, inputId) => {
      dispatch(profileActions.handleChange(e, inputId));
    },
    showChangePasswordModal: () => {
      dispatch(show(CHANGE_PASSWORD_MODAL));
    },
    hideChangePasswordModal: () => {
      dispatch(hide(CHANGE_PASSWORD_MODAL));
    },
  })
)
class ProfileAdmin extends Component {
  static propTypes = {
    user: PropTypes.object,
    working: PropTypes.bool,
    changing_password: PropTypes.bool,
    modal_data: PropTypes.object,
    profile_form_data: PropTypes.object,
    error: PropTypes.array,
    success: PropTypes.bool,
    showChangePasswordModal: PropTypes.func.isRequired,
    changePasswordRequest: PropTypes.func.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
  };

  componentDidUpdate({ success }) {
    if (!success && this.props.success) {
      this.props.hideChangePasswordModal();
    }
  }

  render() {
    const {
      user,
      working,
      profile_form_data: profileFormData,
      error,
      success,
      showChangePasswordModal,
      handleSubmit,
      handleClose,
      handleChange,
    } = this.props;

    return (
      <I18n>
        {({ i18n }) => (
          <Fragment>
            <Helmet title={i18n._(t("profile.title")`Administration`)} />
            <StyledPageTitle >
              {i18n._(t("profile.profile_management")`Manage Profile`)}
            </StyledPageTitle>
            <Frame data-testid="profile">
              {user && (
                <StyledFrameContent>
                  <MsgAlert
                    type="success"
                    message={
                      success
                        ? i18n._(
                            t(
                              "profile.success_message"
                            )`User password successfully updated.`
                          )
                        : null
                    }
                  />

                  <ProfileKeyValue
                    keyText={i18n._(t("profile.username")`Username`)}
                    valueText={user.username}
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.password")`Password`)}
                    valueText={
                      <Button primary thin onClick={showChangePasswordModal}>
                        <Trans id="profile.change_password">
                          Change Password
                        </Trans>
                      </Button>
                    }
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.email")`Email`)}
                    valueText={user.email}
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.display_name")`Display Name`)}
                    valueText={user.display_name}
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.role")`Role`)}
                    valueText={user.role}
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.projects")`Projects`)}
                    valueText={
                      user.project.length ? user.project.join(", ") : "-"
                    }
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.sites")`Sites`)}
                    valueText={user.site.length ? user.site.join(", ") : "-"}
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.groups")`Groups`)}
                    valueText={
                      user.responsibility_group.length
                        ? user.responsibility_group.join(", ")
                        : "-"
                    }
                  />
                  <ProfileKeyValue
                    keyText={i18n._(t("profile.language")`Language`)}
                    valueText={<LanguageSelect />}
                  />
                </StyledFrameContent>
              )}

              <ChangePasswordModal
                ref={() => "editUserPasswordModal"}
                modalTitle={i18n._(
                  t(
                    "change_password_modal_data.change_user_password"
                  )`Change User Password`
                )}
                modalSubmitText={i18n._(
                  t(
                    "change_password_modal_data.update_password"
                  )`Update Password`
                )}
                modalSubmit={handleSubmit}
                modalClose={handleClose}
                modalWorking={working}
              >
                <ChangePasswordForm
                  formData={profileFormData}
                  onChange={handleChange}
                  error={error}
                />
              </ChangePasswordModal>
            </Frame>
          </Fragment>
        )}
      </I18n>
    );
  }
}

export default ProfileAdmin;
